import { useEffect, useState } from "react"
import { AlertCircle, X } from "lucide-react"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import type { ClientTheme } from "@/app/lib/themes"
import { primaryButton, secondaryButton } from "@/app/lib/buttonStyles"
import type { Transaction } from "@/app/types/inventory"

// The two interest-type values present in the domain data; blank is also valid.
const INTEREST_TYPES = ["HBP", "PT"]

// Stored decimals arrive as fraction strings ("0.8125"); the form edits percents.
// Round the pre-fill so common fractions render cleanly (0.07 → "7", not
// "7.000000000000001") and round-trip losslessly — an unrelated edit must not
// drift a stored NRI/ORRI value or spuriously mark the row dirty.
function toPercent(fraction: string | null): string {
  if (fraction == null || fraction === "") return ""
  const n = Number(fraction)
  if (Number.isNaN(n)) return ""
  return String(Math.round(n * 1e10) / 1e8)
}

// Edit modal for an existing transaction. `type` is immutable on edit (shown as
// read-only text) — this enforces the DIV-needs-a-prior-position rule, which the
// server only checks on create, without a server change. Follows WellFormModal's
// shell (overlay + blur, ESC close, body scroll lock, full-screen on mobile).
export function TransactionEditModal({
  tx,
  onSubmit,
  onClose,
  saving,
  error,
  theme,
}: {
  tx: Transaction
  onSubmit: (payload: Record<string, unknown>) => void
  onClose: () => void
  saving: boolean
  error: string | null
  theme: ClientTheme
}) {
  const isMobile = useIsMobile()
  const isDiv = tx.type === "DIV"

  const [form, setForm] = useState({
    str: tx.str,
    transaction_date: tx.transaction_date ?? "",
    net_acres: tx.net_acres ?? "",
    nri_percent: toPercent(tx.nri),
    orri_percent: toPercent(tx.orri_reserved),
    interest_type: tx.interest_type ?? "",
    depths: tx.depths ?? "",
    price: tx.price ?? "",
    counterparty: tx.counterparty ?? "",
    book_page: tx.book_page ?? "",
    notes: tx.notes ?? "",
    arti: tx.arti,
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  // Interest type is one of the two real domain values (HBP / PT) or blank. If a
  // row carries a legacy value outside that set, keep it selectable so editing
  // another field never silently rewrites it.
  const interestOptions = [
    { value: "", label: "—" },
    ...(form.interest_type && !INTEREST_TYPES.includes(form.interest_type)
      ? [{ value: form.interest_type, label: form.interest_type }]
      : []),
    ...INTEREST_TYPES.map((o) => ({ value: o, label: o })),
  ]

  // Never dismiss (ESC / overlay / Cancel / X) while a PATCH is in flight.
  const safeClose = () => {
    if (!saving) onClose()
  }

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") safeClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose, saving])

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // type intentionally NOT sent — immutable on edit (protects the DIV-needs-a-
    // prior-position rule, which the server only checks on create).
    onSubmit({
      str: form.str.trim(),
      transaction_date: form.transaction_date || null,
      net_acres: form.net_acres === "" ? null : Number(form.net_acres),
      nri: form.nri_percent === "" ? null : Number(form.nri_percent) / 100,
      orri_reserved: form.orri_percent === "" ? 0 : Number(form.orri_percent) / 100,
      interest_type: form.interest_type || null,
      depths: form.depths || null,
      price: form.price === "" ? null : Number(form.price),
      counterparty: form.counterparty || null,
      book_page: form.book_page || null,
      notes: form.notes || null,
      arti: form.arti,
    })
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={safeClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex flex-col overflow-y-auto ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(680px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? undefined : "90vh",
        }}
      >
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <span
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "15px",
              fontWeight: 600,
              color: theme.textPrimary,
            }}
          >
            Edit {tx.tx_number}
          </span>
          <button
            type="button"
            onClick={safeClose}
            title="Close"
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textMuted,
              padding: "4px",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            {/* Type is immutable on edit — render as read-only text. */}
            <div className="flex flex-col gap-1.5">
              <span style={labelStyle(theme)}>Type</span>
              <div
                style={{
                  ...inputStyle(theme),
                  backgroundColor: theme.bgPage,
                  color: theme.textMuted,
                }}
              >
                {isDiv ? "Divestiture" : tx.type === "ACQ" ? "Acquisition" : tx.type}
              </div>
            </div>
            <Field label="STR" theme={theme}>
              <Input value={form.str} onChange={(v) => set("str", v)} theme={theme} />
            </Field>
            <Field label="Date" theme={theme}>
              <Input
                type="date"
                value={form.transaction_date}
                onChange={(v) => set("transaction_date", v)}
                theme={theme}
              />
            </Field>
            <Field label="Net Acres" theme={theme}>
              <Input
                type="number"
                step="any"
                value={String(form.net_acres)}
                onChange={(v) => set("net_acres", v)}
                theme={theme}
              />
            </Field>
            <Field label="NRI (%)" theme={theme}>
              <Input
                type="number"
                step="any"
                value={form.nri_percent}
                onChange={(v) => set("nri_percent", v)}
                theme={theme}
              />
            </Field>
            {/* ORRI Reserved applies only to divestitures (an acquisition never
                reserves an override) — hidden for ACQ. */}
            {isDiv && (
              <Field label="ORRI Reserved (%)" theme={theme}>
                <Input
                  type="number"
                  step="any"
                  value={form.orri_percent}
                  onChange={(v) => set("orri_percent", v)}
                  theme={theme}
                />
              </Field>
            )}
            <Field label="Interest Status" theme={theme}>
              <Select
                value={form.interest_type}
                onChange={(v) => set("interest_type", v)}
                options={interestOptions}
                theme={theme}
              />
            </Field>
            <Field label="Depths" theme={theme}>
              <Input value={form.depths} onChange={(v) => set("depths", v)} theme={theme} />
            </Field>
            <Field label="Price" theme={theme}>
              <Input
                type="number"
                step="any"
                value={String(form.price)}
                onChange={(v) => set("price", v)}
                theme={theme}
              />
            </Field>
            <Field label="Counterparty" theme={theme}>
              <Input
                value={form.counterparty}
                onChange={(v) => set("counterparty", v)}
                theme={theme}
              />
            </Field>
            <Field label="Book / Page" theme={theme}>
              <Input value={form.book_page} onChange={(v) => set("book_page", v)} theme={theme} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes" theme={theme}>
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={4}
                  style={inputStyle(theme)}
                />
              </Field>
            </div>
            {isDiv && (
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.arti}
                    onChange={(e) => set("arti", e.target.checked)}
                  />
                  <span
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "13px",
                      color: theme.textPrimary,
                    }}
                  >
                    ARTI (All Rights, Title & Interest)
                  </span>
                </label>
              </div>
            )}
          </div>

          {error && <ErrorBanner theme={theme} message={error} />}
        </div>

        <div className="px-6 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={safeClose}
            disabled={saving}
            className="btn w-full sm:w-auto"
            style={{
              ...secondaryButton(theme),
              justifyContent: "center",
              opacity: saving ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn w-full sm:w-auto"
            style={{ ...primaryButton(theme), justifyContent: "center", opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Local presentational helpers (mirrors the per-page convention) ───────────

function inputStyle(theme: ClientTheme): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 11px",
    borderRadius: "8px",
    backgroundColor: theme.bgSurface2,
    border: `1px solid ${theme.borderColor}`,
    color: theme.textPrimary,
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    outline: "none",
  }
}

function labelStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "11px",
    fontWeight: 600,
    color: theme.textDim,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  }
}

function Field({
  label,
  theme,
  children,
}: {
  label: string
  theme: ClientTheme
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span style={labelStyle(theme)}>{label}</span>
      {children}
    </label>
  )
}

function Input({
  value,
  onChange,
  type = "text",
  placeholder,
  step,
  theme,
}: {
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  step?: string
  theme: ClientTheme
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      step={step}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle(theme)}
    />
  )
}

function Select({
  value,
  onChange,
  options,
  theme,
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  theme: ClientTheme
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle(theme), cursor: "pointer" }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function ErrorBanner({ theme, message }: { theme: ClientTheme; message: string }) {
  return (
    <div
      className="mt-4 rounded-xl p-3 flex items-center gap-2"
      style={{
        backgroundColor: `${theme.brandAccent}12`,
        border: `1px solid ${theme.brandAccent}40`,
      }}
    >
      <AlertCircle size={15} style={{ color: theme.brandAccent }} />
      <span style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.brandAccent }}>
        {message}
      </span>
    </div>
  )
}
